import React, { useState } from "react";
import { useForm } from "react-hook-form";
import AppSelect, { AppSelectController } from "./AppSelect";

const FRUIT_OPTIONS = [
	{ id: "apple", name: "Apple" },
	{ id: "banana", name: "Banana" },
	{ id: "orange", name: "Orange" },
	{ id: "grape", name: "Grape" },
	{ id: "mango", name: "Mango" },
];

const COUNTRY_OPTIONS = [
	{ id: "se", name: "Sweden" },
	{ id: "no", name: "Norway" },
	{ id: "dk", name: "Denmark" },
	{ id: "fi", name: "Finland" },
];

const AppSelectExample: React.FC = () => {
	const [simpleValue, setSimpleValue] = useState<string>("");
	const [withLabelValue, setWithLabelValue] = useState<string>("banana");
	const [withErrorValue, setWithErrorValue] = useState<string>("");

	const { control, handleSubmit } = useForm({
		defaultValues: {
			country: "se",
			fruit: "",
		},
	});

	const onSubmit = (data: any) => {
		alert(JSON.stringify(data, null, 2));
	};

	return (
		<div className="space-y-8 p-6">
			<div>
				<h3 className="text-lg font-semibold mb-4">AppSelect Component Examples</h3>

				<div className="space-y-6">
					{/* Basic select without label */}
					<div>
						<h4 className="text-md font-medium mb-2">Basic Select (No Label)</h4>
						<AppSelect
							options={FRUIT_OPTIONS}
							value={simpleValue}
							onChange={setSimpleValue}
							placeholder="Select a fruit"
							getOptionLabel={(option) => option.name}
							getOptionValue={(option) => option.id}
							width="300px"
						/>
						<p className="text-sm text-gray-600 mt-2">Selected: {simpleValue || "None"}</p>
					</div>

					{/* Select with label and initial value */}
					<div>
						<h4 className="text-md font-medium mb-2">With Label and Initial Value</h4>
						<AppSelect
							options={FRUIT_OPTIONS}
							value={withLabelValue}
							onChange={setWithLabelValue}
							label="Favorite Fruit"
							placeholder="Select a fruit"
							getOptionLabel={(option) => option.name}
							getOptionValue={(option) => option.id}
							width="300px"
						/>
						<p className="text-sm text-gray-600 mt-2">Selected: {withLabelValue}</p>
					</div>

					{/* Select with mandatory indicator */}
					<div>
						<h4 className="text-md font-medium mb-2">Mandatory Field</h4>
						<AppSelect
							options={COUNTRY_OPTIONS}
							value={withErrorValue}
							onChange={setWithErrorValue}
							label="Country"
							placeholder="Select a country"
							getOptionLabel={(option) => option.name}
							getOptionValue={(option) => option.id}
							mandatory
							width="300px"
						/>
					</div>

					{/* Select with error message */}
					<div>
						<h4 className="text-md font-medium mb-2">With Error Message</h4>
						<AppSelect
							options={COUNTRY_OPTIONS}
							value=""
							onChange={() => {}}
							label="Country"
							placeholder="Select a country"
							getOptionLabel={(option) => option.name}
							getOptionValue={(option) => option.id}
							errors="This field is required"
							mandatory
							width="300px"
						/>
					</div>

					{/* Disabled select */}
					<div>
						<h4 className="text-md font-medium mb-2">Disabled Select</h4>
						<AppSelect
							options={FRUIT_OPTIONS}
							value="apple"
							onChange={() => {}}
							label="Disabled Fruit"
							placeholder="Select a fruit"
							getOptionLabel={(option) => option.name}
							getOptionValue={(option) => option.id}
							disabled
							width="300px"
						/>
					</div>

					{/* Full width select */}
					<div>
						<h4 className="text-md font-medium mb-2">Full Width Select</h4>
						<AppSelect
							options={COUNTRY_OPTIONS}
							value={withLabelValue}
							onChange={setWithLabelValue}
							label="Country"
							placeholder="Select a country"
							getOptionLabel={(option) => option.name}
							getOptionValue={(option) => option.id}
							width="100%"
						/>
					</div>

					{/* React Hook Form integration */}
					<div>
						<h4 className="text-md font-medium mb-4">React Hook Form Integration</h4>
						<form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
							<AppSelectController
								control={control}
								name="country"
								label="Country"
								placeholder="Select a country"
								options={COUNTRY_OPTIONS}
								getOptionLabel={(option) => option.name}
								getOptionValue={(option) => option.id}
								mandatory
								width="300px"
							/>
							<AppSelectController
								control={control}
								name="fruit"
								label="Fruit"
								placeholder="Select a fruit"
								options={FRUIT_OPTIONS}
								getOptionLabel={(option) => option.name}
								getOptionValue={(option) => option.id}
								width="300px"
							/>
							<button
								type="submit"
								className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
							>
								Submit Form
							</button>
						</form>
					</div>

					{/* Side by side layout */}
					<div>
						<h4 className="text-md font-medium mb-2">Side by Side Layout</h4>
						<div className="flex gap-4">
							<AppSelect
								options={FRUIT_OPTIONS}
								value={simpleValue}
								onChange={setSimpleValue}
								label="First Choice"
								placeholder="Select a fruit"
								getOptionLabel={(option) => option.name}
								getOptionValue={(option) => option.id}
								width="100%"
							/>
							<AppSelect
								options={COUNTRY_OPTIONS}
								value={withErrorValue}
								onChange={setWithErrorValue}
								label="Second Choice"
								placeholder="Select a country"
								getOptionLabel={(option) => option.name}
								getOptionValue={(option) => option.id}
								width="100%"
							/>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
};

export default AppSelectExample;
